import { FetchAccountAddressesParams, PermissionSimulatorParams } from 'api/account';
import { FetchBulkActionsParams } from 'api/bulkActions';
import { FetchDataHygieneParams } from 'api/contacts/data-hygiene';
import { FetchContactFilesParams } from 'api/contacts/single';
import {
    FetchAdvancedEmailTemplateParams,
    FetchEngageMessagesParams,
    FetchMessageTemplateParams,
    MergeFieldsParams,
} from 'api/engage';
import { FetchAccountEventsParams } from 'api/events';
import { FetchImportDeletionStatsParams, FetchImportsParams } from 'api/imports';
import { CampaignPayoutsParams, PayoutsParams } from 'api/payouts';
import { FetchPledgesParams } from 'api/pledges';
import { FetchPromoCodesParams } from 'api/promoCodes';
import { FetchReportSchedulesParams, FetchReportsParams } from 'api/reports';
import { FetchTaskHistoryParams, FetchTasksParams } from 'api/tasks';
import { FetchTicketBundlesParams, FetchTicketTypesParams } from 'api/tickets';
import { FetchPlansParams } from 'api/transactionPlans';
import { FetchLegalEntityFilesParams } from 'api/verification';
import {
    DateOption as AccountOverviewChartDateOption,
    DateOptionsDateRange,
} from 'components/account-overview-chart/types';
import { ContactType } from 'constants/contacts';
import { DateOption as HouseholdChartDateOption } from 'constants/households';
import { EngageMessageChannel, IntegrationMappingContext, ReportResource } from 'types/domain';
import { FilterParams } from 'types/params';

export const accountQueryKeys = {
    init: () => ['init'],
    all: () => ['account'],
    current: (accountId: number) => [...accountQueryKeys.all(), accountId],
    campaigns: (accountId: number) => [...accountQueryKeys.current(accountId), 'campaigns'],
    campaignsWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.campaigns(accountId),
        params,
    ],
    campaignsByActiveStatusWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.campaigns(accountId),
        params,
    ],
    chapters: (accountId: number, params: { page?: number } = {}) => [
        ...accountQueryKeys.current(accountId),
        'chapters',
        params,
    ],
    shortCodes: (accountId: number) => [...accountQueryKeys.current(accountId), 'short-codes'],
    shortCodesWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.shortCodes(accountId),
        params,
    ],
    shortCode: (accountId: number, shortCodeId: string) => [
        ...accountQueryKeys.shortCodes(accountId),
        shortCodeId,
    ],
    paymentAccounts: (accountId: number) => [
        ...accountQueryKeys.current(accountId),
        'payment-accounts',
    ],
    onboarding: (accountId: number) => [...accountQueryKeys.current(accountId), 'onboarding'],
    onboardingCharts: (accountId: number) => [...accountQueryKeys.onboarding(accountId), 'charts'],
    onboardingChartsByCampaignId: (accountId: number, campaignId: number) => [
        ...accountQueryKeys.onboardingCharts(accountId),
        campaignId,
        'charts',
    ],
    onboardingChartsFilters: (
        accountId: number,
        campaignId: number,
        date: AccountOverviewChartDateOption,
        customDate: DateOptionsDateRange,
    ) => [
        ...accountQueryKeys.onboardingChartsByCampaignId(accountId, campaignId),
        date,
        customDate,
        'charts',
    ],
    accountMemberRoles: (accountId: number) => [
        ...accountQueryKeys.current(accountId),
        'accountMemberRoles',
    ],
    accountPermissionSimulator: (
        accountId: number,
        params: PermissionSimulatorParams = { roles: [] },
    ) => [...accountQueryKeys.addresses(accountId), params],
    addresses: (accountId: number) => [...accountQueryKeys.current(accountId), 'addresses'],
    addressesWithFilters: (accountId: number, params: FetchAccountAddressesParams = {}) => [
        ...accountQueryKeys.addresses(accountId),
        params,
    ],
    addressById: (accountId: number, addressId: number) => [
        ...accountQueryKeys.addresses(accountId),
        addressId,
    ],
    contacts: (accountId: number) => [...accountQueryKeys.current(accountId), 'contacts'],
    contactsWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.contacts(accountId),
        params,
    ],
    contactsFirstTimeSupporters: (accountId: number) => [
        ...accountQueryKeys.contacts(accountId),
        'firstTimeSupporters',
    ],
    contactById: (accountId: number, contactId: number) => [
        ...accountQueryKeys.contacts(accountId),
        contactId,
    ],
    companyContacts: (accountId: number, companyId: number) => [
        ...accountQueryKeys.contactById(accountId, companyId),
        'companyContacts',
    ],
    companyContactsWithFilters: (
        accountId: number,
        companyId: number,
        params: FilterParams = {},
    ) => [...accountQueryKeys.companyContacts(accountId, companyId), params],
    contactActivities: (accountId: number, contactId: number) => [
        ...accountQueryKeys.contactById(accountId, contactId),
        'activities',
    ],
    contactActivitiesWithFilter: (accountId: number, contactId: number, filter: string) => [
        ...accountQueryKeys.contactActivities(accountId, contactId),
        filter,
    ],
    contactDonorSearch: (accountId: number, contactId: number) => [
        ...accountQueryKeys.contactById(accountId, contactId),
        'donorSearch',
    ],
    contactRelationships: (accountId: number, contactId: number) => [
        ...accountQueryKeys.contactById(accountId, contactId),
        'relationships',
    ],
    contactRelationshipsWithFilters: (
        accountId: number,
        contactId: number,
        params: FilterParams = {},
    ) => [...accountQueryKeys.contactRelationships(accountId, contactId), params],
    contactRelationshipById: (accountId: number, contactId: number, relationshipId: number) => [
        ...accountQueryKeys.contactRelationships(accountId, contactId),
        relationshipId,
    ],
    contactGivingSummaryChart: (
        accountId: number,
        contactId: number,
        dateOption: string,
        contactType: ContactType,
    ) => [
        ...accountQueryKeys.contactById(accountId, contactId),
        dateOption,
        contactType,
        'giving_summary_chart',
    ],
    contactFiles: (accountId: number, contactId: number) => [
        ...accountQueryKeys.contactById(accountId, contactId),
        'files',
    ],
    contactFilesWithParams: (
        accountId: number,
        contactId: number,
        params: FetchContactFilesParams,
    ) => [...accountQueryKeys.contactFiles(accountId, contactId), params],

    legalEntityFiles: (accountId: number) => [
        ...accountQueryKeys.current(accountId),
        'legal_entity_files',
    ],
    legalEntityFilesWithParams: (accountId: number, params: FetchLegalEntityFilesParams) => [
        ...accountQueryKeys.legalEntityFiles(accountId),
        params,
    ],

    contactEoyReports: (accountId: number, contactId: number) => [
        ...accountQueryKeys.contactById(accountId, contactId),
        'eoy_reports',
    ],
    dataHygiene: (accountId: number) => [...accountQueryKeys.current(accountId), 'data_hygiene'],
    dataHygieneActivation: (accountId: number) => [
        ...accountQueryKeys.dataHygiene(accountId),
        'data_hygiene_activation',
    ],
    dataHygieneMetrics: (accountId: number) => [
        ...accountQueryKeys.dataHygiene(accountId),
        'data_hygiene_metrics',
    ],
    dataHygieneDuplicates: (accountId: number) => [
        ...accountQueryKeys.dataHygiene(accountId),
        'data_hygiene_duplicates',
    ],
    dataHygieneAddresses: (accountId: number) => [
        ...accountQueryKeys.dataHygiene(accountId),
        'data_hygiene_addresses',
    ],
    dataHygieneAddressesWithFilters: (accountId: number, params: FetchDataHygieneParams = {}) => [
        ...accountQueryKeys.dataHygieneAddresses(accountId),
        params,
    ],
    dataHygieneEmails: (accountId: number) => [
        ...accountQueryKeys.dataHygiene(accountId),
        'data_hygiene_emails',
    ],
    dataHygieneEmailsWithFilters: (accountId: number, params: FetchDataHygieneParams = {}) => [
        ...accountQueryKeys.dataHygieneEmails(accountId),
        params,
    ],
    dataHygienePhoneNumbers: (accountId: number) => [
        ...accountQueryKeys.dataHygiene(accountId),
        'data_hygiene_phonenumbers',
    ],
    dataHygienePhoneNumbersWithFilters: (
        accountId: number,
        params: FetchDataHygieneParams = {},
    ) => [...accountQueryKeys.dataHygienePhoneNumbers(accountId), params],

    engage: (accountId: number) => [...accountQueryKeys.current(accountId), 'engage'],
    engageMessageTemplates: () => ['message_templates'],
    engageMessageTemplatesWithFilters: (params: FetchMessageTemplateParams) => [
        ...accountQueryKeys.engageMessageTemplates(),
        params,
    ],
    engageAccountMessageTemplates: (accountId: number) => [
        ...accountQueryKeys.engage(accountId),
        'message_templates',
    ],
    engageAccountMessageTemplatesWithFilters: (
        accountId: number,
        channel: EngageMessageChannel,
    ) => [
        ...accountQueryKeys.engageAccountMessageTemplates(accountId),
        'message_templates',
        channel,
    ],
    engageAdvancedMessageTemplates: (accountId: number) => [
        ...accountQueryKeys.engage(accountId),
        'advanced_message_templates',
    ],
    engageAdvancedMessageTemplatesWithFilters: (
        accountId: number,
        params: FetchAdvancedEmailTemplateParams,
    ) => [...accountQueryKeys.engageAdvancedMessageTemplates(accountId), params],
    engageAdvancedMessageTemplateById: (accountId: number, id: string) => [
        ...accountQueryKeys.engageAdvancedMessageTemplates(accountId),
        id,
    ],
    engageConfiguration: (accountId: number) => [...accountQueryKeys.engage(accountId), 'config'],
    engageMessageNumbers: (accountId: number) => [
        ...accountQueryKeys.engage(accountId),
        'message_numbers',
    ],
    engageMessages: (accountId: number) => [...accountQueryKeys.engage(accountId), 'messages'],
    engageMessageById: (accountId: number, messageId: number) => [
        ...accountQueryKeys.engageMessages(accountId),
        messageId,
    ],
    engageDeliveries: (accountId: number, messageId: number) => [
        accountQueryKeys.engageMessageById(accountId, messageId),
        'deliveries',
    ],
    engageDeliveryById: (accountId: number, messageId: number, deliveryId: number) => [
        ...accountQueryKeys.engageDeliveries(accountId, messageId),
        deliveryId,
    ],
    engageMessagePreviewStatus: (accountId: number, messageId: number) => [
        ...accountQueryKeys.engageMessageById(accountId, messageId),
        'preview_status',
    ],
    engageMessageTracking: (accountId: number, messageId: number) => [
        ...accountQueryKeys.engageMessageById(accountId, messageId),
        'tracking',
    ],
    engageMessageTrackingWithFilters: (
        accountId: number,
        messageId: number,
        params: FilterParams = {},
    ) => [...accountQueryKeys.engageMessageTracking(accountId, messageId), params],
    engageMessageFulfillmentStats: (accountId: number, messageId: number) => [
        ...accountQueryKeys.engageMessageById(accountId, messageId),
        'fulfillment_stats',
    ],
    engageMessageMetrics: (accountId: number, messageId: number) => [
        ...accountQueryKeys.engageMessageById(accountId, messageId),
        'metrics',
    ],
    engageMessagesWithFilters: (accountId: number, params: FetchEngageMessagesParams = {}) => [
        ...accountQueryKeys.engageMessages(accountId),
        params,
    ],
    engageSmsKeywords: (accountId: number) => [
        ...accountQueryKeys.engage(accountId),
        'sms_keywords',
    ],
    engageMessageStats: (accountId: number, messageId: number) => [
        ...accountQueryKeys.engageMessageById(accountId, messageId),
        'stats',
    ],
    engageMessageStatsWithFilters: (
        accountId: number,
        messageId: number,
        filters: {
            limit?: number;
            page?: number;
            filterBy?: string;
        },
    ) => [...accountQueryKeys.engageMessageStats(accountId, messageId), filters],
    engageMessageProofs: (accountId: number, messageId: number) => [
        ...accountQueryKeys.engageMessageById(accountId, messageId),
        'proofs',
    ],
    engageMailPieceById: (accountId: number, messageId: number, mailPieceId: number) => [
        ...accountQueryKeys.engageMessageById(accountId, messageId),
        mailPieceId,
    ],
    events: (accountId: number) => [...accountQueryKeys.current(accountId), 'events'],
    eventsWithFilters: (accountId: number, params: FetchAccountEventsParams) => [
        ...accountQueryKeys.events(accountId),
        params,
    ],
    letterProofCheckoutDetails: (accountId: number, messageId: number, mailPieceId: number) => [
        ...accountQueryKeys.engageMailPieceById(accountId, messageId, mailPieceId),
        'letterProofCheckoutDetails',
    ],
    engageMessageStripeSession: (accountId: number, messageId: number) => [
        ...accountQueryKeys.engageMessageById(accountId, messageId),
        'stripeSession',
    ],
    engageMessageCheckoutDetails: (accountId: number, messageId: number) => [
        ...accountQueryKeys.engageMessageById(accountId, messageId),
        'checkoutDetails',
    ],
    engageMessageProofStripeSession: (accountId: number, messageId: number) => [
        ...accountQueryKeys.engageMessageById(accountId, messageId),
        'proofStripeSession',
    ],
    engageDomains: (accountId: number) => [...accountQueryKeys.engage(accountId), 'domains'],
    engageSenders: (accountId: number) => [...accountQueryKeys.engage(accountId), 'senders'],
    plans: (accountId: number) => [...accountQueryKeys.current(accountId), 'plans'],
    planById: (accountId: number, planId: number) => [...accountQueryKeys.plans(accountId), planId],
    plansStats: (accountId: number) => [...accountQueryKeys.plans(accountId), 'stats'],
    plansWithFilters: (accountId: number, params: FetchPlansParams = {}) => [
        ...accountQueryKeys.plans(accountId),
        params,
    ],
    imports: (accountId: number) => [...accountQueryKeys.current(accountId), 'imports'],
    importsWithFilters: (accountId: number, params: FetchImportsParams = {}) => [
        ...accountQueryKeys.imports(accountId),
        params,
    ],
    importById: (accountId: number, importId: number) => [
        ...accountQueryKeys.imports(accountId),
        importId,
    ],
    importDeletionStats: (
        accountId: number,
        importId: number,
        deletionType: FetchImportDeletionStatsParams['deletionType'],
    ) => [...accountQueryKeys.importById(accountId, importId), deletionType, 'stats'],
    tickets: (accountId: number) => [...accountQueryKeys.current(accountId), 'tickets'],
    ticketTypes: (accountId: number) => [...accountQueryKeys.tickets(accountId), 'ticketTypes'],
    ticketTypesWithFilters: (accountId: number, params: FetchTicketTypesParams) => [
        ...accountQueryKeys.ticketTypes(accountId),
        params,
    ],
    ticketById: (accountId: number, ticketId: number) => [
        ...accountQueryKeys.tickets(accountId),
        ticketId,
    ],
    ticketsWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.tickets(accountId),
        params,
    ],
    ticketBundles: (accountId: number) => [...accountQueryKeys.current(accountId), 'ticketBundles'],
    ticketBundlesWithFilters: (accountId: number, params: FetchTicketBundlesParams) => [
        ...accountQueryKeys.ticketBundles(accountId),
        params,
    ],
    bundlePurchases: (accountId: number) => [
        ...accountQueryKeys.current(accountId),
        'bundlePurchases',
    ],
    bundlePurchasesById: (accountId: number, bundleId: number) => [
        ...accountQueryKeys.bundlePurchases(accountId),
        bundleId,
    ],
    bundlePurchasesWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.bundlePurchases(accountId),
        params,
    ],
    households: (accountId: number) => [...accountQueryKeys.current(accountId), 'households'],
    householdsWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.households(accountId),
        params,
    ],
    householdById: (accountId: number, householdId: number) => [
        ...accountQueryKeys.households(accountId),
        householdId,
    ],
    householdChart: (accountId: number, householdId: number, date: HouseholdChartDateOption) => [
        ...accountQueryKeys.householdById(accountId, householdId),
        date,
        'chart',
    ],
    tags: (accountId: number) => [...accountQueryKeys.current(accountId), 'tags'],
    tagById: (accountId: number, tagId: number) => [...accountQueryKeys.tags(accountId), tagId],
    tagsWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.tags(accountId),
        params,
    ],
    reportColumns: (accountId: number) => [accountId, 'reportColumns'],
    reportColumnsByType: (accountId: number, reportType: string) => [
        ...accountQueryKeys.reportColumns(accountId),
        reportType,
    ],
    members: (accountId: number) => [...accountQueryKeys.current(accountId), 'members'],
    membersWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.members(accountId),
        params,
    ],
    membersWithInvitesWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.members(accountId),
        params,
    ],
    memberById: (accountId: number, memberId: number) => [
        ...accountQueryKeys.members(accountId),
        memberId,
    ],
    pendingUsers: (accountId: number) => [...accountQueryKeys.current(accountId), 'pendingUsers'],
    pendingUsersWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.pendingUsers(accountId),
        params,
    ],
    inviteById: (accountId: number, inviteId: string) => [
        ...accountQueryKeys.members(accountId),
        inviteId,
    ],
    crmMembers: (accountId: number) => [...accountQueryKeys.current(accountId), 'crmMembers'],
    crmMembersWithFilters: (accountId: number, params: FilterParams = {}) => [
        ...accountQueryKeys.crmMembers(accountId),
        params,
    ],
    payouts: (accountId: number) => [...accountQueryKeys.current(accountId), 'payouts'],
    payoutsWithFilters: (accountId: number, params: PayoutsParams) => [
        ...accountQueryKeys.payouts(accountId),
        params,
    ],
    payoutAccount: (accountId: number) => [...accountQueryKeys.payouts(accountId), 'payoutAccount'],
    promoCodes: (accountId: number) => [...accountQueryKeys.current(accountId), 'promoCodes'],
    promoCodesWithFilters: (accountId: number, params: FetchPromoCodesParams) => [
        ...accountQueryKeys.promoCodes(accountId),
        params,
    ],
    campaignPayouts: (accountId: number) => [
        ...accountQueryKeys.payouts(accountId),
        'campaignPayouts',
    ],
    campaignPayoutsWithFilters: (accountId: number, params: CampaignPayoutsParams) => [
        ...accountQueryKeys.campaignPayouts(accountId),
        params,
    ],
    integrations: (accountId: number) => [...accountQueryKeys.current(accountId), 'integrations'],
    integrationMappings: (accountId: number, type: IntegrationMappingContext) => [
        ...accountQueryKeys.integrations(accountId),
        'integrationMappings',
        type,
    ],
    mergeFields: (accountId: number) => [...accountQueryKeys.current(accountId), 'mergeFields'],
    mergeFieldsWithParams: (accountId: number, params: MergeFieldsParams) => [
        ...accountQueryKeys.mergeFields(accountId),
        params,
    ],
    tasks: (accountId: number) => [...accountQueryKeys.current(accountId), 'tasks'],
    tasksWithFilters: (accountId: number, params: FetchTasksParams) => [
        ...accountQueryKeys.tasks(accountId),
        params,
    ],
    taskIdsWithFilters: (accountId: number, params: FetchTasksParams) => [
        ...accountQueryKeys.tasks(accountId),
        'ids',
        params,
    ],
    taskById: (accountId: number, taskId: number) => [...accountQueryKeys.tasks(accountId), taskId],
    taskHistory: (accountId: number, taskId: number, params: FetchTaskHistoryParams) => [
        ...accountQueryKeys.taskById(accountId, taskId),
        'history',
        params,
    ],
    pledges: (accountId: number) => [...accountQueryKeys.current(accountId), 'pledges'],
    pledgesWithParams: (accountId: number, params: FetchPledgesParams = {}) => [
        ...accountQueryKeys.pledges(accountId),
        params,
    ],
    pledgeById: (accountId: number, pledgeId: number) => [
        ...accountQueryKeys.pledges(accountId),
        pledgeId,
    ],
    pledgeStats: (accountId: number) => [...accountQueryKeys.pledges(accountId), 'stats'],
    teamMembers: (accountId: number) => [...accountQueryKeys.current(accountId), 'teamMembers'],
    teamMembersWithFilters: (accountId: number, params: FilterParams) => [
        ...accountQueryKeys.teamMembers(accountId),
        params,
    ],
    teams: (accountId: number) => [...accountQueryKeys.current(accountId), 'teams'],
    teamsWithFilters: (accountId: number, params: FilterParams) => [
        ...accountQueryKeys.teams(accountId),
        params,
    ],
    appeals: (accountId: number) => [...accountQueryKeys.current(accountId), 'appeals'],
    appealsWithFilters: (accountId: number, params: FilterParams) => [
        ...accountQueryKeys.appeals(accountId),
        params,
    ],
    appealById: (accountId: number, appealId: number) => [
        ...accountQueryKeys.appeals(accountId),
        appealId,
    ],
    funds: (accountId: number) => [...accountQueryKeys.current(accountId), 'funds'],
    fundsWithFilters: (accountId: number, params: FilterParams) => [
        ...accountQueryKeys.funds(accountId),
        params,
    ],
    fundById: (accountId: number, fundId: number) => [...accountQueryKeys.funds(accountId), fundId],
    externalLabels: (accountId: number) => [
        ...accountQueryKeys.current(accountId),
        'externalLabels',
    ],
    externalLabelsWithFilters: (
        accountId: number,
        params: FilterParams & { labels?: string[] },
    ) => [...accountQueryKeys.externalLabels(accountId), params],
    matches: (accountId: number) => [...accountQueryKeys.current(accountId), 'matches'],
    matchesWithFilters: (accountId: number, params: FilterParams) => [
        ...accountQueryKeys.matches(accountId),
        params,
    ],
    billingPlans: (accountId: number) => [...accountQueryKeys.current(accountId), 'billingPlans'],
    reports: (accountId: number) => [...accountQueryKeys.current(accountId), 'reports'],
    reportsWithFilters: (accountId: number, params: FetchReportsParams = {}) => [
        ...accountQueryKeys.reports(accountId),
        params,
    ],
    reportTemplates: (accountId: number) => [...accountQueryKeys.current(accountId), 'templates'],
    reportSchemas: (accountId: number) => [...accountQueryKeys.current(accountId), 'schemas'],
    reportSchemaByResource: (accountId: number, resource: ReportResource) => [
        ...accountQueryKeys.reportSchemas(accountId),
        resource,
    ],
    reportById: (accountId: number, reportId: number) => [
        ...accountQueryKeys.reports(accountId),
        reportId,
    ],
    reportStats: (accountId: number, reportId: number, params: FetchReportsParams = {}) => [
        ...accountQueryKeys.reportById(accountId, reportId),
        'stats',
        params,
    ],
    reportSchedules: (accountId: number, reportId: number) => [
        ...accountQueryKeys.reportById(accountId, reportId),
        'schedules',
    ],
    reportSchedulesWithFilters: (
        accountId: number,
        reportId: number,
        params: FetchReportSchedulesParams = {},
    ) => [...accountQueryKeys.reportSchedules(accountId, reportId), params],
    reportScheduleById: (accountId: number, reportId: number, scheduleId: number) => [
        ...accountQueryKeys.reportSchedules(accountId, reportId),
        scheduleId,
    ],
    bulkActions: (accountId: number) => [...accountQueryKeys.current(accountId), 'bulkActions'],
    bulkActionsWithFilters: (accountId: number, params: FetchBulkActionsParams) => [
        ...accountQueryKeys.bulkActions(accountId),
        params,
    ],
    bulkActionById: (accountId: number, bulkActionId: number) => [
        ...accountQueryKeys.bulkActions(accountId),
        bulkActionId,
    ],
    quickBooks: (accountId: number) => [...accountQueryKeys.current(accountId), 'quickBooks'],
    quickBooksRebuild: (accountId: number) => [
        ...accountQueryKeys.quickBooks(accountId),
        'rebuild',
    ],
    quickBooksStatus: (accountId: number) => [...accountQueryKeys.quickBooks(accountId), 'status'],
    quickBooksSetup: (accountId: number) => [...accountQueryKeys.quickBooks(accountId), 'setup'],
    quickBooksDetails: (accountId: number) => [
        ...accountQueryKeys.quickBooks(accountId),
        'details',
    ],
    quickBooksRules: (accountId: number) => [...accountQueryKeys.quickBooks(accountId), 'rules'],
    quickBooksRuleById: (accountId: number, ruleId: number) => [
        ...accountQueryKeys.quickBooksRules(accountId),
        ruleId,
    ],
    recommendedActions: (accountId: number) => [
        ...accountQueryKeys.current(accountId),
        'recommendedActions',
    ],
    requiredChecklist: (accountId: number) => [
        ...accountQueryKeys.current(accountId),
        'requiredChecklist',
    ],
};
